// index.tsx 또는 App.tsx 상단
import 'handsontable/dist/handsontable.full.min.css'; // 가장 먼저!

import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import MainPage from './components/MainPage';
import CreateDailyReport from './components/CreateDailyReport';
import Login from './components/Login';
import MobileDispatchList from './components/MobileDispatchList';
import DispatchReportSidebar from './components/DispatchReportSidebar';
import DispatchReportPage from './components/DispatchReportPage';
import ContainerDispatchSidebar from './components/ContainerDispatchSidebar';
import ContainerDispatchReportPage from './components/ContainerDispatchReportPage';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import CompanyInfoPage from './components/CompanyInfoPage';
import ContainerMasterPage from './components/ContainerMasterPage';
import VehicleInfoPage from './components/VehicleInfoPage';
import IntegratedReportPage from './components/IntegratedReportPage';
import SettlementReportPage from './components/SettlementReportPage';
import StatementTemplatePage from './components/StatementTemplatePage';
import ExcelImport from './components/ExcelImport';
import ReminderOverlay from './components/ReminderOverlay';
import UnassignedUpcomingModal, { UnassignedUpcomingItem } from './components/UnassignedUpcomingModal';
import SearchPage from './components/SearchPage';
import ManualPage from './components/ManualPage';
import TruckingRatePage from './components/TruckingRatePage';
import DashboardPage from './components/DashboardPage';
import NoticeBoardPage from './components/NoticeBoardPage';
import SmsPage from './components/SmsPage';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/ko';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import CompanyScheduleBoardPage from './components/CompanyScheduleBoardPage';
import ScheduleBoardPage from './components/ScheduleBoardPage';
import StorageExplorerPage from './components/StorageExplorerPage';
import RequireAdmin, { ADMIN_PAGE_UNLOCK_STORAGE_KEY } from './components/RequireAdmin';
import AdminPage from './components/AdminPage';
import AppCustomer from './AppCustomer';
import ProcurementPage from './components/ProcurementPage';
import { doc, getDoc } from 'firebase/firestore';
import { socket } from './socket';
import { getCacheManager } from './utils/cacheManager';

//getCacheManager();


declare global {
  interface Window {
    _testSocket: any;
  }
}
window._testSocket = socket;

/** 배포 시 false — 조달부 메뉴 비활성 (개발 시 true로 전환) */
const PROCUREMENT_MENU_ENABLED = true

/** 배포 시 false — 컨테이너 배차일보 메뉴 비활성 (개발 시 true로 전환) */
const CONTAINER_DISPATCH_MENU_ENABLED = true;

// 라우터를 사용하는 내부 컴포넌트
function AppContent() {
  const [selectedMenu, setSelectedMenu] = useState('홈');
  const [selectedSubMenu, setSelectedSubMenu] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedReportDate, setSelectedReportDate] = useState<string | undefined>(undefined);
  const [selectedContainerDate, setSelectedContainerDate] = useState<string | undefined>(undefined);
  const [statementSidebarCollapsed, setStatementSidebarCollapsed] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [lastReminderTime, setLastReminderTime] = useState<number>(Date.now());
  const [showUnassignedUpcoming, setShowUnassignedUpcoming] = useState(false);
  const [unassignedUpcomingItems, setUnassignedUpcomingItems] = useState<UnassignedUpcomingItem[]>([]);
  const lastUnassignedFetchRef = useRef<number>(Date.now());
  const [showSearch, setShowSearch] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  // 프론트 버전 감지 (캐시로 인한 구버전 사용 방지 도움)
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [versionCheckedAt, setVersionCheckedAt] = useState<number>(0);
  const [versionOutdated, setVersionOutdated] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [isStandalone, setIsStandalone] = useState(false);

  // 예전 AdminPage가 넣던 전역 #ht-dropdown-style(.handsontable max-height:70vh) — 정산일보 등 다른 표 레이아웃을 망가뜨림
  useEffect(() => {
    document.getElementById('ht-dropdown-style')?.remove();
  }, []);

  // PWA(설치형) 실행 감지: standalone/fullscreen/minimal-ui 또는 iOS navigator.standalone
  useEffect(() => {
    try {
      const checkStandalone = () => {
        const queries = [
          '(display-mode: standalone)',
          '(display-mode: fullscreen)',
          '(display-mode: minimal-ui)',
          '(display-mode: window-controls-overlay)'
        ];
        const matches = queries.some(q => (window.matchMedia && window.matchMedia(q).matches));
        const iosStandalone = (window.navigator as any)?.standalone === true;
        setIsStandalone(matches || iosStandalone);
      };
      checkStandalone();
    } catch (e) {
      setIsStandalone(false);
    }
  }, []);

  // 정산일보 등에서 전역 검색 모달을 열 수 있도록 커스텀 이벤트 리스너 등록
  useEffect(() => {
    const handler = (e: any) => {
      const open = !!(e && e.detail);
      setShowSearch(open);
    };
    window.addEventListener('global-search-toggle', handler as any);
    return () => {
      window.removeEventListener('global-search-toggle', handler as any);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  // 버전 폴링: /version.json을 주기적으로 조회하여 변경되면 새로고침 안내
  useEffect(() => {
    let timer: any;
    let alerted = false;
    const fetchVersion = async () => {
      try {
        const res = await fetch(`${process.env.PUBLIC_URL || ''}/version.json?ts=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        const v = String(json?.version || '');
        if (!appVersion) {
          setAppVersion(v);
          setVersionCheckedAt(Date.now());
          return;
        }
        if (v && appVersion && v !== appVersion && !alerted) {
          alerted = true;
          setVersionOutdated(true);
        }
      } catch {}
    };
    fetchVersion();
    timer = setInterval(fetchVersion, 60000); // 60초마다 체크
    const handleOnline = () => fetchVersion();
    window.addEventListener('online', handleOnline);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', handleOnline);
    };
  }, [appVersion]);

  // 사용자 정보 로드
  useEffect(() => {
    if (!user?.email) {
      setUserInfo(null);
      setUserInfoLoaded(true);
      return;
    }
    
    // localStorage에서 캐시된 사용자 정보 확인
    const cachedUserInfo = localStorage.getItem(`userInfo_${user.email}`);
    if (cachedUserInfo) {
      try {
        const userData = JSON.parse(cachedUserInfo);
        setUserInfo(userData);
        setUserInfoLoaded(true);
        
        // 화주 사용자인 경우 즉시 리턴
        if (userData?.role === 'customer') {
          return;
        }
      } catch (error) {
        console.error('캐시된 사용자 정보 파싱 실패:', error);
      }
    }
    
    const loadUserInfo = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.email));
        const userData = userDoc.data() || null;
        setUserInfo(userData);
        
        // localStorage에 사용자 정보 캐시
        if (userData) {
          localStorage.setItem(`userInfo_${user.email}`, JSON.stringify(userData));
        }
        
        // 화주 사용자인 경우 즉시 리다이렉션
        if (userData?.role === 'customer') {
          setUserInfoLoaded(true);
          return;
        }
      } catch (error) {
        console.error('사용자 정보 로드 실패:', error);
        setUserInfo(null);
      } finally {
        setUserInfoLoaded(true);
      }
    };
    
    // 캐시된 정보가 없는 경우에만 Firestore에서 로드
    if (!cachedUserInfo) {
      loadUserInfo();
    }
  }, [user]);

  // 알림 타이머 설정
  useEffect(() => {
    const checkReminder = () => {
      const now = Date.now();
      const oneHour = 60 * 60 * 1000; // 1시간을 밀리초로
      
      if (now - lastReminderTime >= oneHour) {
        setShowReminder(true);
        setLastReminderTime(now);
      }
    };

    // 1분마다 체크
    const interval = setInterval(checkReminder, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [lastReminderTime]);

  // 앞으로 14일 미배차 항목 — 2시간마다 조회, 항목이 있을 때만 팝업 (모바일·화주·비로그인 제외)
  useEffect(() => {
    if (!user || isMobile || !userInfoLoaded) return;
    if (userInfo?.role === 'customer') return;
    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
    const tick = async () => {
      const now = Date.now();
      if (now - lastUnassignedFetchRef.current < TWO_HOURS_MS) return;
      lastUnassignedFetchRef.current = now;
      try {
        const res = await fetch('/api/dispatch-reports/unassigned-items-upcoming?days=14');
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data.items) ? data.items : [];
        if (list.length > 0) {
          setUnassignedUpcomingItems(list);
          setShowUnassignedUpcoming(true);
        }
      } catch (e) {
        console.error('미배차 사전 알림 조회 실패:', e);
      }
    };
    const id = window.setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [user, isMobile, userInfo, userInfoLoaded]);

  const menuItems = [
    {
      id: '배차',
      label: '- 배차',
      subMenus: [
        { label: '배차일보' },
        { label: '컨테이너 배차일보', disabled: !CONTAINER_DISPATCH_MENU_ENABLED },
        { label: '생성' }
      ]
    },
    {
      id: '정산',
      label: '- 정산',
      subMenus: [
        { label: '정산일보' },
        { label: '명세서양식' }
      ]
    },
    {
      id: '조달부',
      label: '- 조달실',
      disabled: !PROCUREMENT_MENU_ENABLED,
    },
    { id: '대시보드', label: '- 대시보드' },
    { id: '매뉴얼', label: '- 매뉴얼' },
    {
      id: '정보',
      label: '- 정보',
      subMenus: [
        { label: '거래처정보' },
        { label: '컨테이너' },
        { label: '차량정보' },
        { label: '운임표' },
        { label: '문자' },
        { label: '캐시DB' }
      ]
    },
  ];

  const handleMenuClick = (menuId: string) => {
    setSelectedMenu(menuId === '배차내역' ? '배차' : menuId);
    setSidebarOpen(false);
    if (menuId === '대시보드') {
      setSelectedSubMenu('');
      navigate('/dashboard');
    } else if (menuId === '매뉴얼') {
      setSelectedSubMenu('');
      navigate('/manual');
    } else if (menuId === '조달부') {
      if (!PROCUREMENT_MENU_ENABLED) return;
      setSelectedMenu('조달부');
      setSelectedSubMenu('');
      setSidebarOpen(false);
      navigate('/procurement');
    }
  };

  const handleSubMenuClick = (subMenu: string) => {
    if (subMenu === "통합일보") {
      return; // 비활성화: 리뉴얼된 정산일보 사용으로 통합일보 사용 중단 예정
    }
    if (subMenu === "생성") {
      setSelectedMenu("배차");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/dispatch/create');
    } else if (subMenu === "배차일보") {
      setSelectedMenu("배차");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(true);
      setSelectedReportDate(undefined);
      navigate('/dispatch/report');
    } else if (subMenu === "컨테이너 배차일보") {
      if (!CONTAINER_DISPATCH_MENU_ENABLED) return;
      setSelectedMenu("배차");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(true);
      setSelectedContainerDate(undefined);
      navigate('/dispatch/container');
    } else if (subMenu === "거래처정보") {
      setSelectedMenu("정보");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/info/company');
    } else if (subMenu === "컨테이너") {
      setSelectedMenu("정보");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/info/container');
    } else if (subMenu === "차량정보") {
      setSelectedMenu("정보");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/info/vehicle');
    } else if (subMenu === "운임표") {
      setSelectedMenu("정보");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/info/rate');
    } else if (subMenu === "문자") {
      setSelectedMenu("정보");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/info/sms');
    } else if (subMenu === "정산일보") {
      setSelectedMenu("정산");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/settlement/report');
    } else if (subMenu === "명세서양식") {
      setSelectedMenu("정산");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/settlement/template');
    } else if (subMenu === "캐시DB") {
      setSelectedMenu("정보");
      setSelectedSubMenu(subMenu);
      setSidebarOpen(false);
      navigate('/info/cache');
    }
  };

  const handleHomeClick = () => {
    setSelectedMenu('홈');
    setSelectedSubMenu('');
    setSidebarOpen(false);
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      try {
        sessionStorage.removeItem(ADMIN_PAGE_UNLOCK_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      // localStorage 캐시 지우기
      if (user?.email) {
        localStorage.removeItem(`userInfo_${user.email}`);
      }
      await signOut(auth);
      setUser(null);
      setUserInfo(null);
      setUserInfoLoaded(false);
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  // 알림 닫기 핸들러
  const handleReminderClose = () => {
    setShowReminder(false);
  };

  const handleUnassignedUpcomingClose = () => {
    setShowUnassignedUpcoming(false);
  };

  // Ctrl+1로 전체화면 토글, Ctrl+2로 사이드바 접기/펼치기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+1로 전체화면 토글
      if (e.ctrlKey && e.key === '1') {
        e.preventDefault();
        setIsFullScreen(!isFullScreen);
      }
      // Ctrl+2로 사이드바 접기/펼치기
      if (e.ctrlKey && e.key === '2') {
        e.preventDefault();
        if (selectedMenu === '배차' && (selectedSubMenu === '배차일보' || selectedSubMenu === '컨테이너 배차일보')) {
          setSidebarCollapsed(!sidebarCollapsed);
        } else if (selectedMenu === '정산' && selectedSubMenu === '정산일보') {
          setStatementSidebarCollapsed(!statementSidebarCollapsed);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedMenu, selectedSubMenu, sidebarCollapsed, isFullScreen, statementSidebarCollapsed]);

  // URL 변경에 따라 사이드바 상태 업데이트
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/') {
      setSelectedMenu('홈');
      setSelectedSubMenu('');
    } else if (path === '/dashboard') {
      setSelectedMenu('대시보드');
      setSelectedSubMenu('');
    } else if (path === '/manual') {
      setSelectedMenu('매뉴얼');
      setSelectedSubMenu('');
    } else if (path === '/dispatch/integrated') {
      setSelectedMenu('배차');
      setSelectedSubMenu('통합일보');
    } else if (path === '/dispatch/report') {
      setSelectedMenu('배차');
      setSelectedSubMenu('배차일보');
      setSidebarOpen(true);
    } else if (path === '/dispatch/container') {
      setSelectedMenu('배차');
      setSelectedSubMenu('컨테이너 배차일보');
      setSidebarOpen(true);
    } else if (path === '/dispatch/create') {
      setSelectedMenu('배차');
      setSelectedSubMenu('생성');
    } else if (path === '/info/company') {
      setSelectedMenu('정보');
      setSelectedSubMenu('거래처정보');
    } else if (path === '/info/container') {
      setSelectedMenu('정보');
      setSelectedSubMenu('컨테이너');
    } else if (path === '/info/vehicle') {
      setSelectedMenu('정보');
      setSelectedSubMenu('차량정보');
    } else if (path === '/info/rate') {
      setSelectedMenu('정보');
      setSelectedSubMenu('운임표');
    } else if (path === '/info/cache') {
      setSelectedMenu('정보');
      setSelectedSubMenu('캐시DB');
    } else if (path === '/settlement/report') {
      setSelectedMenu('정산');
      setSelectedSubMenu('정산일보');
    } else if (path === '/settlement/template') {
      setSelectedMenu('정산');
      setSelectedSubMenu('명세서양식');
    } else if (path === '/procurement' || path.startsWith('/procurement/')) {
      setSelectedMenu('조달부');
      setSelectedSubMenu('');
    } else if (path === '/shared-drive') {
      setSelectedMenu('공유파일함');
      setSelectedSubMenu('');
    } else if (path === '/admin') {
      setSelectedMenu('관리자');
      setSelectedSubMenu('');
    }
  }, [location.pathname]);

  const isAdmin = user?.email === 'admin@swlogis.it.kr';

  // 로딩 상태 처리
  if (!authChecked) {
    // 모바일 설치형(PWA)에서는 인앱 스플래시를 숨겨 이중 노출 방지
    if (isMobile && isStandalone) {
      return null;
    }
    return (
      <div style={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff' }}>
        <div className="splash-logo logo-pulse">
          <img src={require('./assets/logo.png')} alt="성우로지스" style={{ width: 110, height: 60, borderRadius: 8, objectFit: 'cover' }} />
          <span style={{ fontWeight: 700, fontSize: 22, color: '#1976d2', letterSpacing: 1 }}>성우로지스</span>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Login onLogin={() => {}} />;
  }
  if (!userInfoLoaded) {
    if (isMobile && isStandalone) {
      return null;
    }
    return (
      <div style={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff' }}>
        <div className="splash-logo logo-pulse">
          <img src={require('./assets/logo.png')} alt="성우로지스" style={{ width: 110, height: 60, borderRadius: 8, objectFit: 'cover' }} />
          <span style={{ fontWeight: 700, fontSize: 22, color: '#1976d2', letterSpacing: 1 }}>성우로지스</span>
        </div>
      </div>
    );
  }

  // 전역 버전 불일치 강제 안내 모달(팝업) - 반드시 확인하도록
  const VersionModal = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999999, display: versionOutdated ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', padding: 28, borderRadius: 10, minWidth: 320, boxShadow: '0 6px 24px rgba(0,0,0,0.18)', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12, color: '#111' }}>새 버전 안내</div>
        <div style={{ fontSize: 14, color: '#444', marginBottom: 18 }}>최신 버전이 배포되었습니다. 최신 기능과 안정화를 위해 새로고침이 필요합니다.</div>
        <button onClick={() => window.location.reload()} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', minWidth: 160 }}>지금 새로고침</button>
      </div>
    </div>
  );
  // userInfo가 있으면 role 체크, 없으면 기본 사내 사용자로 간주
  if (userInfo && userInfo.role === 'customer') {
    return (
      <>
        <AppCustomer />
        <VersionModal />
      </>
    );
  }
  // 사내 사용자 + 모바일: 전체 화면 배차 내역 전용 화면으로 전환
  if (isMobile && (!userInfo || userInfo.role !== 'customer')) {
    return <MobileDispatchList />;
  }
  // 사내 기본 서비스 (userInfo가 없거나, role이 없는 경우)
  // ...사내 UI 렌더링...

  // 화주가 아닌 사용자 또는 role이 없는 사용자(기본 사내 사용자)는 웹서비스 UI 표시
  if (!userInfo || userInfo.role !== 'customer') {
    // 일반 웹서비스 (화주가 아닌 사용자 + role이 없는 기본 사용자)
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <div className={`App${isFullScreen ? ' fullscreen' : ''}`}>
          {/* 사이드바 */}
          <div className="sidebar">
            <div className="company-name" onClick={handleHomeClick} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0 8px 0', width: '100%', justifyContent: 'center', marginBottom: 8 }}>
              <img src={require('./assets/logo.png')} alt="성우로지스" style={{ width: 90, height: 45, borderRadius: 8, objectFit: 'cover' }} />
              <span style={{ fontWeight: 700, fontSize: 19, color: '#1976d2', letterSpacing: 1 }}>성우로지스</span>
            </div>
            <ul className="menu-list" style={{ padding: 0, margin: 0 }}>
                  {menuItems.map((item, idx) => {
                    const menuDisabled = !!(item as { disabled?: boolean }).disabled;
                    return (
                <li key={item.id} style={{ margin: 0, padding: 0 }}>
                  {/* 생성-정산 사이 구분선 */}
                  {item.id === '정산' && (
                    <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '6px 0' }} />
                  )}
                  {/* 명세서양식-대시보드 사이 구분선 */}
                  {item.id === '대시보드' && idx > 0 && menuItems[idx - 1].id === '정산' && (
                    <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '6px 0' }} />
                  )}
                  {/* 대시보드-매뉴얼 사이 구분선 */}
                  {item.id === '매뉴얼' && idx > 0 && menuItems[idx - 1].id === '대시보드' && (
                    <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '6px 0' }} />
                  )}
                  {/* 매뉴얼-정보 사이 구분선 */}
                  {item.id === '정보' && idx > 0 && menuItems[idx - 1].id === '매뉴얼' && (
                    <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '6px 0' }} />
                  )}
                  <div
                    className={`menu-item${!menuDisabled && selectedMenu === item.id ? ' selected' : ''}${menuDisabled ? ' disabled' : ''}`}
                    onClick={() => !menuDisabled && handleMenuClick(item.id)}
                    style={{
                      padding: '7px 0 7px 18px',
                      fontSize: 15,
                      ...(menuDisabled ? { color: '#9e9e9e', cursor: 'not-allowed', opacity: 0.8 } : {}),
                    }}
                  >
                    <span>{item.label.replace(/^- /, '')}</span>
                  </div>
                  {item.subMenus && (
                    <ul className="submenu-list" style={{ padding: 0, margin: 0 }}>
                      {item.subMenus.map((subMenu, subIdx) => {
                        const disabled = (subMenu as { label: string; disabled?: boolean }).disabled;
                        return (
                          <li
                            key={subMenu.label}
                            className={`submenu-item${!disabled && selectedSubMenu === subMenu.label && selectedMenu === item.id ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
                            onClick={() => !disabled && handleSubMenuClick(subMenu.label)}
                            style={{
                              padding: '5px 0 5px 32px',
                              fontSize: 14,
                              ...(disabled ? { color: '#9e9e9e', cursor: 'not-allowed', opacity: 0.8 } : {})
                            }}
                          >
                            <span>{subMenu.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {/* 정보 메뉴 바로 아래에 구분선과 공유파일함 메뉴 추가 */}
                  {item.id === '정보' && (
                    <>
                      <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '6px 0' }} />
                      <div
                        className={`menu-item${selectedMenu === '공유파일함' ? ' selected' : ''}`}
                        onClick={() => {
                          setSelectedMenu('공유파일함');
                          setSelectedSubMenu('');
                          setSidebarOpen(false);
                          navigate('/shared-drive');
                        }}
                        style={{ padding: '7px 0 7px 18px', fontSize: 15 }}
                      >
                        <span>공유파일함</span>
                      </div>
                      {isAdmin && (
                        <div
                          className={`menu-item${selectedMenu === '관리자' ? ' selected' : ''}`}
                          onClick={() => {
                            setSelectedMenu('관리자');
                            setSelectedSubMenu('');
                            setSidebarOpen(false);
                            navigate('/admin');
                          }}
                          style={{ padding: '7px 0 7px 18px', fontSize: 15 }}
                        >
                          <span>관리자</span>
                        </div>
                      )}
                    </>
                  )}
                </li>
                    );
                  })}
            </ul>
          {/* 사이드바 하단: 로그인한 아이디, 버전, 로그아웃 버튼 */}
          <div style={{ position: 'absolute', bottom: 50, left: 0, width: '100%', textAlign: 'center' }}>
            {/* 로그인한 아이디 */}
            <div style={{ color: '#b0b0b0', fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
              {user?.email ? `${user.email}` : user?.uid ? `${user.uid}` : ''}
            </div>
            {/* 버전 표기 */}
            <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 10 }}>v{appVersion || '...'}</div>
            {/* 로그아웃 버튼 */}
            <button
              onClick={handleLogout}
              style={{ fontSize: 13, padding: '3px 6px', background: '#eee', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Logout
            </button>
          </div>
          </div>
          {/* 메인 컨텐츠 */}
          <div className="main-content">
            <main>
              {/* 라우터 방식으로 변경 */}
              <Routes>
                <Route path="/" element={<MainPage />} />
                {/* 사내 사용자 모바일 전용 경로 */}
                <Route path="/mobile/dispatch" element={<MobileDispatchList />} />
                <Route path="/dispatch/create" element={
                  <CreateDailyReport isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/dispatch/report" element={
                  <DispatchReportPage
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                    selectedDate={selectedReportDate}
                    setSelectedDate={setSelectedReportDate}
                  />
                } />
                <Route path="/dispatch/container" element={
                  <ContainerDispatchReportPage
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                    selectedDate={selectedContainerDate}
                    setSelectedDate={setSelectedContainerDate}
                    sidebarCollapsed={sidebarCollapsed}
                  />
                } />
                <Route path="/dispatch/integrated" element={
                  <IntegratedReportPage isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/info/company" element={
                  <CompanyInfoPage isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/info/container" element={
                  <ContainerMasterPage isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/info/vehicle" element={
                  <VehicleInfoPage isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/info/rate" element={
                  <TruckingRatePage isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/settlement/report" element={
                  <SettlementReportPage 
                    isFullScreen={isFullScreen} 
                    setIsFullScreen={setIsFullScreen}
                    statementSidebarCollapsed={statementSidebarCollapsed}
                    setStatementSidebarCollapsed={setStatementSidebarCollapsed}
                  />
                } />
                <Route path="/settlement/template" element={
                  <StatementTemplatePage supplier={{
                    registrationNumber: '',
                    name: '',
                    ceo: '',
                    address: '',
                    phone: '',
                    fax: ''
                  }} />
                } />
                <Route path="/procurement" element={
                  <ProcurementPage isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                } />
                <Route path="/procurement/items" element={<Navigate to="/procurement?tab=items" replace />} />
                <Route path="/procurement/destinations" element={<Navigate to="/procurement?tab=destinations" replace />} />
                <Route path="/procurement/invoices" element={<Navigate to="/procurement?tab=invoices" replace />} />
                <Route path="/procurement/shipments" element={<Navigate to="/procurement?tab=shipments" replace />} />
                <Route path="/info/cache" element={<ExcelImport />} />
                <Route path="/info/sms" element={<SmsPage />} />
                <Route path="/manual" element={<ManualPage />} />
                <Route path="/dashboard" element={
                  <DashboardPage
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                  />
                } />
                <Route path="/notice" element={<NoticeBoardPage />} />
                <Route path="/company-schedule" element={<CompanyScheduleBoardPage />} />
                <Route path="/schedule-board" element={<ScheduleBoardPage />} />
                <Route path="/shared-drive" element={<StorageExplorerPage />} />
                <Route path="/admin" element={
                  <RequireAdmin>
                    <AdminPage />
                  </RequireAdmin>
                } />
              </Routes>
            </main>
          </div>
          {/* 오른쪽 사이드바 */}
          {selectedMenu === '배차' && selectedSubMenu === '배차일보' ? (
            <DispatchReportSidebar
              open={sidebarOpen}
              collapsed={sidebarCollapsed}
              selectedDate={selectedReportDate}
              onClose={() => setSidebarOpen(false)}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
              onSelectDate={(date) => {
                setSelectedReportDate(date);
              }}
            />
          ) : null}
          {selectedMenu === '배차' && selectedSubMenu === '컨테이너 배차일보' ? (
            <ContainerDispatchSidebar
              open={sidebarOpen}
              collapsed={sidebarCollapsed}
              selectedDate={selectedContainerDate}
              onClose={() => setSidebarOpen(false)}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
              onSelectDate={(date) => {
                setSelectedContainerDate(date);
              }}
            />
          ) : null}
          
          {showReminder && <ReminderOverlay onClose={handleReminderClose} />}
          <UnassignedUpcomingModal
            open={showUnassignedUpcoming}
            items={unassignedUpcomingItems}
            onClose={handleUnassignedUpcomingClose}
          />
          {showSearch && <SearchPage onClose={() => setShowSearch(false)} />}
        </div>
      </LocalizationProvider>
    );
  }

  // 기본 로딩 상태 (사용자 정보가 명확하지 않은 경우)
  return <div style={{ textAlign: 'center', marginTop: 100 }}>로딩 중...</div>;
}

// 메인 App 컴포넌트 - BrowserRouter로 감싸기만 함
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
